/**
 * 统一错误处理模块
 * 提供 AppError 基类和便捷的错误创建方法
 */
import { SDK_ERRORS, ERROR_SOURCE } from './codes.js'

/**
 * 应用错误类
 */
export class AppError extends Error {
  constructor(options = {}) {
    const {
      code = 'SYS_999',
      message = '未知错误',
      source = ERROR_SOURCE.SYSTEM,
      details = null,
      originalError = null,
      show = true  // 是否显示给用户
    } = options

    super(message)
    this.name = 'AppError'
    this.code = code
    this.source = source
    this.details = details
    this.originalError = originalError
    this.show = show
    this.timestamp = Date.now()

    // 保留原始堆栈
    if (originalError instanceof Error) {
      this.stack = originalError.stack
    }
  }

  /**
   * 是否是 SDK 错误
   */
  isSDKError() {
    return this.source === ERROR_SOURCE.SDK
  }

  /**
   * 是否是网络错误
   */
  isNetworkError() {
    return this.source === ERROR_SOURCE.NETWORK
  }

  /**
   * 转换为可序列化的对象
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      source: this.source,
      details: this.details,
      timestamp: this.timestamp
    }
  }
}

/**
 * SDK 错误
 */
export class SDKError extends AppError {
  constructor(options = {}) {
    super({
      ...options,
      source: ERROR_SOURCE.SDK
    })
    this.name = 'SDKError'
  }
}

/**
 * 网络错误
 */
export class NetworkError extends AppError {
  constructor(options = {}) {
    super({
      ...options,
      source: ERROR_SOURCE.NETWORK
    })
    this.name = 'NetworkError'
  }
}

/**
 * 业务错误
 */
export class BusinessError extends AppError {
  constructor(options = {}) {
    super({
      ...options,
      source: ERROR_SOURCE.BUSINESS
    })
    this.name = 'BusinessError'
  }
}

/**
 * 创建错误（便捷方法）
 * @param {string|Object} codeOrError - 错误码或错误定义对象
 * @param {string} message - 自定义错误消息
 * @param {Error} originalError - 原始错误
 */
export function createError(codeOrError, message, originalError) {
  let code, source, defaultMessage

  if (typeof codeOrError === 'string') {
    // 通过错误码查找
    const errorDef = SDK_ERRORS[codeOrError]
    if (errorDef) {
      code = errorDef.code
      source = errorDef.source
      defaultMessage = errorDef.message
    } else {
      code = codeOrError
      source = ERROR_SOURCE.SYSTEM
      defaultMessage = '未知错误'
    }
  } else if (codeOrError && typeof codeOrError === 'object') {
    code = codeOrError.code
    source = codeOrError.source
    defaultMessage = codeOrError.message
  } else {
    code = 'SYS_999'
    source = ERROR_SOURCE.SYSTEM
    defaultMessage = '未知错误'
  }

  return new AppError({
    code,
    source,
    message: message || defaultMessage,
    originalError
  })
}

/**
 * 包装 SDK 错误
 */
export function wrapSDKError(errorDef, originalError, message) {
  return new SDKError({
    code: errorDef.code,
    message: message || errorDef.message,
    originalError,
    show: true
  })
}

/**
 * 包装网络错误
 */
export function wrapNetworkError(originalError, message) {
  return new NetworkError({
    code: 'NET_001',
    message: message || '网络请求失败',
    originalError,
    show: true
  })
}

/**
 * 判断是否为 AppError
 */
export function isAppError(error) {
  return error instanceof AppError
}

export default {
  AppError,
  SDKError,
  NetworkError,
  BusinessError,
  createError,
  wrapSDKError,
  wrapNetworkError,
  isAppError
}
